import { SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import React, { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import {
  ConversationList as BaseConversationList,
  EmptyResultTypeEnum,
  EmptyResultView,
} from '@components';
import {
  ChatNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { XOR } from '@types';
import type { Conversation } from '@models/Chats/ConversationModel';
import type { ConversationList } from '@models/Chats/ConversationListModel';
import type { SearchedConversationList } from '@models/Chats/SearchedConversationListModel';

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flexGrow: 1,
    px: ['$6', '$0'],
    pb: props.bottom,
  };
}

interface IConversationsListProps {
  list: XOR<ConversationList, SearchedConversationList>;
  refresh?(): Promise<void>;
  fetchMore(): Promise<void>;
  onPress(item: Conversation): void;
}

type NavigationPropType = ChatNavigatorNavigationPropType<'Chats'>;

export const ConversationsList = observer<IConversationsListProps>(
  ({ list, refresh, fetchMore, onPress }) => {
    const insets = useSafeAreaInsets();
    const nav = useNavigation<NavigationPropType>();

    const navigateToFindFriend = useCallback(() => {
      nav.navigate(screens.FindFriend);
    }, []);

    const isLoading = list.fetch.inProgress && list.isEmpty;

    let emptyResultType: EmptyResultTypeEnum =
      EmptyResultTypeEnum.Error;

    if (list.fetch.isError) {
      emptyResultType = EmptyResultTypeEnum.Error;
    } else if (list.isEmpty) {
      emptyResultType = list.searchQuery
        ? EmptyResultTypeEnum.NoResult
        : EmptyResultTypeEnum.NoFriends;
    }

    return (
      <BaseConversationList
        contentContainerSx={contentContainerStyle(insets)}
        data={list.asArray}
        onEndReached={fetchMore}
        onRefresh={refresh}
        isRefreshing={list.refresh?.inProgress}
        isLoading={isLoading}
        isLoadingMore={list.fetchMore.inProgress}
        onPress={onPress}
        ListEmptyComponent={() => (
          <EmptyResultView
            type={emptyResultType}
            onPress={navigateToFindFriend}
          />
        )}
      />
    );
  },
);
